import { useState, useRef, useCallback, useEffect } from 'react'
import { Search } from 'lucide-react'
import { searchGuide, type GuideSearchResult } from '../../lib/api'

interface SearchBarProps {
  onSelect: (slug: string) => void
}

export function SearchBar({ onSelect }: SearchBarProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<GuideSearchResult[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Close dropdown on Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsOpen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleInputChange = useCallback(
    (value: string) => {
      setQuery(value)

      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }

      if (value.length < 2) {
        setResults([])
        setIsOpen(false)
        return
      }

      timerRef.current = setTimeout(async () => {
        setLoading(true)
        try {
          const data = await searchGuide(value)
          setResults(data)
          setIsOpen(true)
        } catch (err) {
          console.error('Search failed:', err)
          setResults([])
        } finally {
          setLoading(false)
        }
      }, 300)
    },
    []
  )

  const handleSelect = useCallback(
    (slug: string) => {
      setIsOpen(false)
      setQuery('')
      setResults([])
      onSelect(slug)
    },
    [onSelect]
  )

  const handleBlur = useCallback(() => {
    // Small delay to allow click handling on dropdown items
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setIsOpen(false)
      }
    }, 200)
  }, [])

  /**
   * Highlight matched query term in excerpt text by wrapping in <mark>.
   */
  function highlightExcerpt(text: string, term: string) {
    if (!term || term.length < 2) return text
    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    const parts = text.split(regex)
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-primary/20 text-foreground rounded px-0.5">
          {part}
        </mark>
      ) : (
        part
      )
    )
  }

  return (
    <div ref={containerRef} className="relative w-full" onBlur={handleBlur}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder="Search guide..."
          className="w-full pl-10 pr-4 py-2.5 bg-secondary border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition-colors"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-muted-foreground/30 border-t-primary rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Dropdown results */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto z-50">
          {results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              No results found
            </div>
          ) : (
            results.map((result) => (
              <button
                key={result.slug}
                onMouseDown={(e) => {
                  e.preventDefault()
                  handleSelect(result.slug)
                }}
                className="w-full text-left px-4 py-3 hover:bg-secondary transition-colors border-b border-border last:border-0"
              >
                <div className="text-sm font-medium text-foreground">{result.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                  {highlightExcerpt(result.excerpt, query)}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
